import { createRouter, createWebHistory } from 'vue-router'
import { auth } from '@/utils/auth'
import { isAuthOnlyRoute } from '@/constants/routes'
import { getCustomer } from '@/utils/supabase/auth'

// Dynamic imports
const Application = () => import('@/views/application/Application.vue')

// Route imports
import { appointmentRoutes } from './appointments'
import { assistantRoutes } from './assistants'
import { audioRoutes } from './audio'
import { authRoutes } from './auth'
import { callRoutes } from './calls'
import { dashboardRoutes } from './dashboard'
import { documentRoutes } from './documents'
import { faqRoutes } from './faqs'
import { insuranceRoutes } from './insurance'
import { locationRoutes } from './locations'
import { organizationRoutes } from './organizations'
import { patientRoutes } from './patients'
import { profileRoutes } from './profile'
import { staffRoutes } from './staff'
import { verificationRoutes } from './verifications'
import { faxRoutes } from './faxes'
import { questionnaireRoutes } from './questionnaires'
import { workflowRoutes } from './workflows'

import { billingRoutes } from './billing'
import { onboardingRoutes } from './onboarding'

const routes = [
  ...authRoutes,
  ...onboardingRoutes,
  {
    path: '/',
    component: Application,
    meta: { requiresAuth: true },
    children: [
      ...appointmentRoutes,
      ...assistantRoutes,
      ...audioRoutes,
      ...callRoutes,
      ...dashboardRoutes,
      ...documentRoutes,
      ...faqRoutes,
      ...insuranceRoutes,
      ...locationRoutes,
      ...organizationRoutes,
      ...patientRoutes,
      ...profileRoutes,
      ...staffRoutes,
      ...verificationRoutes,
      ...faxRoutes,
      ...questionnaireRoutes,
      ...workflowRoutes,
      ...billingRoutes
    ]
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard to handle authentication and page titles
router.beforeEach(async (to, from, next) => {
  // Set page title only if explicitly set in route meta
  if (to.meta && 'title' in to.meta) {
    document.title = `${to.meta.title} | VoxHealth`
  }

  // Handle authentication
  const isAuthenticated = !!auth.getSession()

  // Redirect to login if not authenticated and route requires auth
  if (to.matched.some(record => record.meta.requiresAuth) && !isAuthenticated) {
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }

  // Redirect to dashboard if trying to access auth pages while authenticated
  if (isAuthenticated && isAuthOnlyRoute(to.name)) {
    next({ name: 'Dashboard' })
    return
  }

  // Check for onboarding if authenticated and not already on onboarding route
  if (isAuthenticated && (to.name === 'Dashboard' || to.name === 'Onboarding')) {
    try {
      const customer = await getCustomer()
      if (to.name === 'Dashboard' && !customer) {
        next({ name: 'Onboarding' })
        return
      } else if (to.name === 'Onboarding' && customer) {
        next({ name: 'Dashboard' })
        return
      }
    } catch (error) {
      console.error('Error checking customer status:', error)
    }
  }

  next()
})

export default router
